import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import './Typesetter.css';
import siteImages from '../images/site';
import AppStoreBadgeSvg from '../AppStoreBadgeSvg';
import qrCode from '../images/TypesetterQrCode.png';
import {
  APP_STORE_ID,
  APP_STORE_URL,
  COFFEE_URL,
  PAGE_PATH,
  PRIVACY_PATH,
  SITE_URL,
  SUPPORT_EMAIL,
  content,
} from './content.mjs';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=optional';
const THEME_COLOR = '#f2e7ba';

/**
 * Per-page <head> management for the Typesetter route inside the shared CRA
 * shell. The static HTML written by scripts/typesetter-postbuild.js already
 * carries these tags for crawlers; this hook keeps them right during
 * client-side navigation and restores the shell's defaults on unmount.
 */
export function usePageChrome({ title, description, path }) {
  useEffect(() => {
    const html = document.documentElement;
    const head = document.head;
    html.classList.add('ts-page-active');

    const previousTitle = document.title;
    document.title = title;

    const added = [];
    const restore = [];

    const ensure = (selector, create) => {
      let el = head.querySelector(selector);
      if (!el) {
        el = create();
        head.appendChild(el);
        added.push(el);
      }
      return el;
    };
    const setAttr = (el, name, value) => {
      restore.push([el, name, el.getAttribute(name)]);
      el.setAttribute(name, value);
    };
    const meta = (name) => () => {
      const m = document.createElement('meta');
      m.name = name;
      return m;
    };

    setAttr(ensure('meta[name="description"]', meta('description')), 'content', description);
    setAttr(ensure('meta[name="theme-color"]', meta('theme-color')), 'content', THEME_COLOR);

    const canonical = ensure('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.rel = 'canonical';
      return l;
    });
    setAttr(canonical, 'href', `${SITE_URL}${path}`);

    const banner = ensure('meta[name="apple-itunes-app"]', meta('apple-itunes-app'));
    setAttr(banner, 'content', `app-id=${APP_STORE_ID}`);

    ensure(`link[href="${FONT_HREF}"]`, () => {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = FONT_HREF;
      return l;
    });

    if (!window.location.hash) {
      // Instant: the class above turns on smooth scrolling, which would
      // otherwise animate a page reached mid-scroll up to the top.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }

    return () => {
      html.classList.remove('ts-page-active');
      document.title = previousTitle;
      restore.forEach(([el, name, value]) => {
        if (value === null) el.removeAttribute(name);
        else el.setAttribute(name, value);
      });
      added.forEach((el) => el.remove());
    };
  }, [title, description, path]);
}

/**
 * Fades each `.ts-reveal` inside `ref` in as it scrolls into view.
 *
 * Only blocks that start below the fold are hidden (`is-pending`), and that
 * happens before the first paint. Anything already on screen when the page
 * mounts is left alone: fading it in would read as pop-in on a tall window.
 */
export function useReveal(ref) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return undefined;
    const targets = Array.from(root.querySelectorAll('.ts-reveal')).filter(
      (el) => el.getBoundingClientRect().top >= window.innerHeight,
    );
    targets.forEach((el) => el.classList.add('is-pending'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('is-pending');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    targets.forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      targets.forEach((el) => el.classList.remove('is-pending'));
    };
  }, [ref]);
}

export function SkipLink() {
  return (
    <a className="ts-skip" href="#ts-main">
      Skip to content
    </a>
  );
}

/** True on a mouse-and-keyboard device, where a QR code beats an App Store link. */
function isDesktop() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const appleMobile = /iPhone|iPad|iPod/.test(navigator.userAgent);
  return finePointer && !appleMobile;
}

function QrDialog({ open, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      dialog.close();
    }
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [open, onClose]);

  const onBackdropClick = (event) => {
    if (event.target === ref.current) onClose();
  };

  return (
    <dialog
      className="ts-dialog"
      ref={ref}
      onClick={onBackdropClick}
      aria-labelledby="ts-qr-title"
    >
      <div className="ts-dialog-card">
        <button
          type="button"
          className="ts-dialog-close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path
              d="M1 1l12 12M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
        <span className="ts-label">{content.qr.label}</span>
        <h2 id="ts-qr-title">{content.qr.heading}</h2>
        {open && (
          <img
            className="ts-qr"
            src={qrCode}
            alt={content.qr.alt}
            width="390"
            height="390"
          />
        )}
        <p className="ts-muted">{content.qr.body}</p>
        <a className="ts-inline-link" href={APP_STORE_URL}>
          {content.qr.link}
        </a>
      </div>
    </dialog>
  );
}

const QrContext = createContext(() => {});

/** Owns the one QR dialog for the page; every App Store link opens it on desktop. */
export function QrProvider({ children }) {
  const [qrOpen, setQrOpen] = useState(false);
  const closeQr = useCallback(() => setQrOpen(false), []);
  const openQr = useCallback(() => setQrOpen(true), []);
  return (
    <QrContext.Provider value={openQr}>
      {children}
      <QrDialog open={qrOpen} onClose={closeQr} />
    </QrContext.Provider>
  );
}

function useStoreClick() {
  const openQr = useContext(QrContext);
  return useCallback(
    (event) => {
      if (!isDesktop()) return;
      event.preventDefault();
      openQr();
    },
    [openQr],
  );
}

/** `home` is true on the marketing page, where the wordmark scrolls to the top
 *  and the section links exist; other pages link back to it instead. */
export function Nav({ home = false }) {
  const [scrolled, setScrolled] = useState(false);
  const onCtaClick = useStoreClick();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const wordmark = (
    <>
      <img
        src={siteImages['typesetter-icon'].src}
        srcSet={siteImages['typesetter-icon'].srcSet}
        sizes="28px"
        alt=""
        width="28"
        height="28"
      />
      <span>{content.appName}</span>
    </>
  );

  return (
    <header className={`ts-nav${scrolled ? ' is-scrolled' : ''}`}>
      <div className="ts-container ts-nav-inner">
        {home ? (
          <a className="ts-wordmark" href="#ts-top" aria-label="Typesetter, back to top">
            {wordmark}
          </a>
        ) : (
          <Link className="ts-wordmark" to={PAGE_PATH} aria-label="Typesetter home">
            {wordmark}
          </Link>
        )}
        {home && (
          <nav aria-label="Page sections">
            <ul className="ts-nav-links">
              {content.nav.links.map((link) => (
                <li key={link.href}>
                  <a href={link.href}>{link.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <a className="ts-btn" href={APP_STORE_URL} onClick={onCtaClick}>
          {content.nav.compactCta}
        </a>
      </div>
    </header>
  );
}

export function AppStoreBadge({ className = '' }) {
  const onClick = useStoreClick();
  return (
    <a
      className={`ts-badge ${className}`.trim()}
      href={APP_STORE_URL}
      onClick={onClick}
    >
      <AppStoreBadgeSvg />
    </a>
  );
}

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="ts-footer">
      <div className="ts-container">
        <ul className="ts-footer-facts">
          {content.footer.facts.map((fact) => (
            <li key={fact}>{fact}</li>
          ))}
        </ul>
        <div className="ts-footer-inner">
          <div className="ts-footer-links">
            <Link to={PRIVACY_PATH}>{content.footer.privacy}</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`}>
              {content.footer.support}: {SUPPORT_EMAIL}
            </a>
            {COFFEE_URL && (
              <a href={COFFEE_URL} rel="noopener">
                {content.footer.coffee}
              </a>
            )}
          </div>
          <p className="ts-footer-copy">
            © {year} {content.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}

export { PAGE_PATH };
